/**
 * React Hook for Google Analytics
 * Provides easy access to analytics tracking in components
 */

import { useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import * as analytics from '../utils/analytics';

/**
 * Hook to automatically track page views on route changes
 */
export function usePageTracking() {
  const location = useLocation();

  useEffect(() => {
    analytics.trackPageView(location.pathname + location.search, document.title);
  }, [location]);
}

/**
 * Hook to provide analytics tracking functions
 */
export function useAnalytics() {
  const trackButton = useCallback((buttonName: string, location?: string) => {
    analytics.trackButtonClick(buttonName, location);
  }, []);

  const trackService = useCallback((serviceId: string, serviceName: string) => {
    analytics.trackServiceView(serviceId, serviceName);
  }, []);

  const trackCartAdd = useCallback((item: {
    id: string;
    name: string;
    price: number;
    category?: string;
  }) => {
    analytics.trackAddToCart(item);
  }, []);

  const trackCartRemove = useCallback((item: {
    id: string;
    name: string;
    price: number;
  }) => {
    analytics.trackRemoveFromCart(item);
  }, []);

  const trackCheckoutStart = useCallback((cartValue: number, itemCount: number) => {
    analytics.trackBeginCheckout(cartValue, itemCount);
  }, []);

  const trackCheckoutProgress = useCallback((step: number, stepName: string) => {
    analytics.trackCheckoutStep(step, stepName);
  }, []);

  const trackOrder = useCallback((data: {
    orderId: string;
    value: number;
    items: Array<{
      id: string;
      name: string;
      price: number;
      quantity: number;
      category?: string;
    }>;
    paymentMethod?: string;
    promoCode?: string;
    discount?: number;
  }) => {
    analytics.trackPurchase(data);
  }, []);

  const trackSearchQuery = useCallback((query: string, resultsCount?: number) => {
    analytics.trackSearch(query, resultsCount);
  }, []);

  const trackPromo = useCallback((code: string, success: boolean, discount?: number) => {
    analytics.trackPromoCode(code, success, discount);
  }, []);

  const trackForm = useCallback((formName: string, success: boolean) => {
    analytics.trackFormSubmission(formName, success);
  }, []);

  const trackUserLogin = useCallback((method: string) => {
    analytics.trackLogin(method);
  }, []);

  const trackUserSignup = useCallback((method: string) => {
    analytics.trackSignup(method);
  }, []);

  const trackUserLogout = useCallback(() => {
    analytics.trackLogout();
  }, []);

  const trackErrorEvent = useCallback((errorMessage: string, errorLocation?: string) => {
    analytics.trackError(errorMessage, errorLocation);
  }, []);

  const trackShareEvent = useCallback((method: string, contentType: string, itemId: string) => {
    analytics.trackShare(method, contentType, itemId);
  }, []);

  const trackReviewSubmission = useCallback((rating: number, itemType: string, itemId: string) => {
    analytics.trackReview(rating, itemType, itemId);
  }, []);

  return {
    trackButton,
    trackService,
    trackCartAdd,
    trackCartRemove,
    trackCheckoutStart,
    trackCheckoutProgress,
    trackOrder,
    trackSearchQuery,
    trackPromo,
    trackForm,
    trackUserLogin,
    trackUserSignup,
    trackUserLogout,
    trackErrorEvent,
    trackShareEvent,
    trackReviewSubmission
  };
}

/**
 * Hook to track engagement time on a page
 */
export function useEngagementTracking(pageName: string) {
  useEffect(() => {
    const startTime = Date.now();

    return () => {
      const endTime = Date.now();
      const engagementSeconds = Math.floor((endTime - startTime) / 1000);
      
      if (engagementSeconds > 0) {
        analytics.trackEngagement(pageName, engagementSeconds);
      }
    };
  }, [pageName]);
}

/**
 * Hook to track performance timing
 */
export function usePerformanceTracking(category: string, variable: string, label?: string) {
  useEffect(() => {
    const startTime = performance.now();

    return () => {
      const endTime = performance.now();
      const durationMs = Math.floor(endTime - startTime);
      
      if (durationMs > 0) {
        analytics.trackTiming(category, variable, durationMs, label);
      }
    };
  }, [category, variable, label]);
}

export default {
  usePageTracking,
  useAnalytics,
  useEngagementTracking,
  usePerformanceTracking
};
